// ── Voice picker ──────────────────────────────────────────────────────────────
// Choose which voice reads the answers. Ported from the mobile app's picker:
//
// - **Auditioning, choosing and committing are three gestures.** Play hears a
//   voice; tapping a row marks it; nothing is saved until "Use this voice". So
//   trying six voices doesn't leave the sixth in effect, and Cancel means
//   something.
// - Playing one stops whatever was playing — two voices at once can't be judged.
// - The sample is fetched through MyAMP (the page never talks to the speech
//   service's CDN). A voice with no sample just has no play button; one that's
//   gone (404) means the list is stale, and the picker says so.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiPause, FiPlay, FiRefreshCw, FiX } from 'react-icons/fi';
import { fetchVoicePreview, VoiceApiError, type VoiceOption } from './voiceApi';
import { describeVoice, resolveVoiceId, saveVoiceSettings, voiceCatalogue } from './speech/speechPrefs';

interface VoicePickerProps {
  /** Called after closing, with whether a voice was saved. */
  onClose: (saved: boolean) => void;
  /** Shown above the list on the first visit, when the picker opens by itself. */
  firstRun?: boolean;
}

const VoicePicker: React.FC<VoicePickerProps> = ({ onClose, firstRun }) => {
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState('');
  const [fetchingPreview, setFetchingPreview] = useState('');

  // Samples play through Web Audio, with the context started inside the click:
  // Safari refuses to start audio after a wait (the sample's download), and an
  // <audio> element would be started after exactly that.
  const contextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);

  const stopPreview = useCallback(() => {
    const source = sourceRef.current;
    sourceRef.current = null;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch {
        // already stopped
      }
    }
    setPlaying('');
  }, []);

  useEffect(
    () => () => {
      contextRef.current?.close().catch(() => {});
      contextRef.current = null;
    },
    [],
  );

  const load = useCallback(async (refresh: boolean) => {
    setLoading(true);
    setError('');
    try {
      const [list, current] = await Promise.all([voiceCatalogue(refresh), resolveVoiceId()]);
      setVoices(list);
      // Opens on the voice already in effect — the stored one, or the default.
      setSelected(previous => previous || current || list[0]?.voiceId || '');
    } catch {
      setError("Couldn't load the voices. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
    return stopPreview;
  }, [load, stopPreview]);

  const play = useCallback(
    async (voiceId: string) => {
      if (playing === voiceId) {
        stopPreview();
        return;
      }
      stopPreview();
      // Before anything is awaited — still inside the click.
      if (!contextRef.current && typeof AudioContext !== 'undefined') contextRef.current = new AudioContext();
      const context = contextRef.current;
      if (context?.state === 'suspended') void context.resume().catch(() => {});
      setFetchingPreview(voiceId);
      try {
        const clip = await fetchVoicePreview(voiceId);
        if (!clip || !context) return;
        const buffer = await context.decodeAudioData(await clip.arrayBuffer());
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(context.destination);
        source.onended = () => {
          if (sourceRef.current === source) stopPreview();
        };
        sourceRef.current = source;
        setPlaying(voiceId);
        source.start();
      } catch (err) {
        stopPreview();
        setError(
          err instanceof VoiceApiError && err.status === 404
            ? 'That voice is no longer available. Refresh the list.'
            : "Couldn't play that sample. Please try again.",
        );
      } finally {
        setFetchingPreview('');
      }
    },
    [playing, stopPreview],
  );

  const [saving, setSaving] = useState(false);
  const save = useCallback(async () => {
    if (!selected) return;
    stopPreview();
    setSaving(true);
    try {
      await saveVoiceSettings({ voiceId: selected });
      onClose(true);
    } catch {
      setError("Couldn't save your voice. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [onClose, selected, stopPreview]);

  const cancel = useCallback(() => {
    stopPreview();
    onClose(false);
  }, [onClose, stopPreview]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [cancel]);

  return (
    <div className="voice-modal" role="presentation" onMouseDown={e => e.target === e.currentTarget && cancel()}>
      <div className="voice-modal__panel" role="dialog" aria-modal="true" aria-labelledby="voice-picker-title">
        <header className="voice-modal__header">
          <h2 id="voice-picker-title" className="voice-modal__title">
            Assistant voice
          </h2>
          <button
            type="button"
            className="voice-modal__icon-btn"
            onClick={() => void load(true)}
            disabled={loading}
            title="Refresh the list"
            aria-label="Refresh the list"
          >
            <FiRefreshCw size={15} className={loading ? 'voice-modal__spin' : ''} />
          </button>
          <button type="button" className="voice-modal__icon-btn" onClick={cancel} title="Close" aria-label="Close">
            <FiX size={16} />
          </button>
        </header>

        {firstRun && (
          <p className="voice-modal__intro">Choose the voice your assistant reads its answers in. You can change it any time in Settings.</p>
        )}
        {error && (
          <p className="voice-modal__error" role="alert">
            {error}
          </p>
        )}

        <ul className="voice-picker__list" role="listbox" aria-label="Voices">
          {voices.map(voice => {
            const { shortName, traits } = describeVoice(voice);
            const isSelected = voice.voiceId === selected;
            return (
              <li
                key={voice.voiceId}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={`voice-picker__row ${isSelected ? 'voice-picker__row--selected' : ''}`}
                onClick={() => setSelected(voice.voiceId)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected(voice.voiceId);
                  }
                }}
              >
                <div className="voice-picker__text">
                  <span className="voice-picker__name">{shortName}</span>
                  {traits && <span className="voice-picker__traits">{traits}</span>}
                  {voice.description && <span className="voice-picker__description">{voice.description}</span>}
                </div>
                {voice.hasPreview && (
                  <button
                    type="button"
                    className="voice-picker__play"
                    onClick={e => {
                      e.stopPropagation();
                      void play(voice.voiceId);
                    }}
                    aria-label={playing === voice.voiceId ? `Stop ${shortName}` : `Play a sample of ${shortName}`}
                    disabled={fetchingPreview === voice.voiceId}
                  >
                    {playing === voice.voiceId ? <FiPause size={14} /> : <FiPlay size={14} />}
                  </button>
                )}
              </li>
            );
          })}
          {!loading && voices.length === 0 && !error && (
            <li className="voice-modal__intro">No voices are available right now.</li>
          )}
        </ul>

        <footer className="voice-modal__footer">
          <button type="button" className="voice-modal__btn" onClick={cancel}>
            {firstRun ? 'Keep the default' : 'Cancel'}
          </button>
          <button type="button" className="voice-modal__btn voice-modal__btn--primary" onClick={() => void save()} disabled={!selected || saving}>
            Use this voice
          </button>
        </footer>
      </div>
    </div>
  );
};

export default VoicePicker;
